import { useState, ReactNode, useRef, useEffect } from 'react'
import { Tooltip as ArkTooltip, type TooltipRootProps as ArkTooltipRootProps } from '@ark-ui/react'
import { classNames as cn } from '~/helpers/string'

export type TooltipProps = {
  content: ReactNode
  trigger?: 'hover' | 'click'
  size?: 'sm' | 'md' | 'lg'
  className?: string
} & ArkTooltipRootProps

const sizeStyles = {
  sm: 'py-1 px-2 rounded-[4px]',
  md: 'py-2.5 px-5 rounded-lg',
  lg: 'py-5 px-7.5 rounded-xl',
}

export function Tooltip(props: TooltipProps) {
  const { children, content, trigger = 'hover', size = 'sm', className = '', ...rest } = props

  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  const isHoverable = typeof window !== 'undefined' && window?.matchMedia('(hover: hover)').matches

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('click', onClickOutside)
    return () => {
      document.removeEventListener('click', onClickOutside)
    }
  }, [])

  return (
    <ArkTooltip.Root
      open={trigger === 'click' || !isHoverable ? isOpen : undefined}
      closeDelay={trigger === 'click' || !isHoverable ? 0 : undefined}
      openDelay={trigger === 'click' || !isHoverable ? 0 : undefined}
      positioning={{ placement: 'top' }}
      {...rest}
    >
      <ArkTooltip.Trigger
        ref={triggerRef}
        asChild
        onClick={(e) => {
          if (trigger === 'click' || !isHoverable) {
            setIsOpen((prev) => !prev)
          }
          e.stopPropagation()
        }}
      >
        {typeof children === 'string' ? <span>{children}</span> : children}
      </ArkTooltip.Trigger>
      <ArkTooltip.Positioner>
        <ArkTooltip.Content
          className={cn('bg-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.15)]', sizeStyles[size], className)}
        >
          <ArkTooltip.Arrow
            style={{
              // @ts-ignore
              '--arrow-size': '8px',
              '--arrow-background': 'white',
            }}
          >
            <ArkTooltip.ArrowTip />
          </ArkTooltip.Arrow>
          {content}
        </ArkTooltip.Content>
      </ArkTooltip.Positioner>
    </ArkTooltip.Root>
  )
}
